/**
 * Shopify Warehouse - Variant Availability
 *
 * Features:
 * - Available, sold-out and invalid variant states
 * - Shopify option-value ID support
 * - Dynamic availability messages
 * - Selected combination availability checks
 * - Suggested alternative selections
 * - Warehouse disabled styling compatibility
 * - Dynamic product rerender support
 *
 * Warehouse remains responsible for variant selection.
 */

(() => {
  "use strict";

  const STATES = {
    AVAILABLE: "available",
    SOLD_OUT: "sold-out",
    INVALID: "invalid"
  };

  const STATE_CLASSES = [
    "variant-option--available",
    "variant-option--sold-out",
    "variant-option--invalid"
  ];

  const DATA_CACHE = new WeakMap();

  /**
   * Get product variant data.
   */
  function getProductData(picker) {
    const sectionId = picker.getAttribute("section-id");

    if (!sectionId) return null;

    const script = document.getElementById(
      `variant-availability-${sectionId}`
    );

    if (!script) return null;

    if (DATA_CACHE.has(script)) {
      return DATA_CACHE.get(script);
    }

    try {
      const data = JSON.parse(script.textContent);

      DATA_CACHE.set(script, data);

      return data;
    } catch (error) {
      console.error(
        "[Variant Availability] Invalid product data:",
        error
      );

      return null;
    }
  }

  /**
   * Get option inputs.
   */
  function getOptionInputs(picker) {
    return Array.from(
      picker.querySelectorAll("[data-option-position]")
    );
  }

  /**
   * Get currently selected options.
   */
  function getSelectedOptions(inputs) {
    const selected = {};

    inputs.forEach(input => {
      const position = Number(
        input.getAttribute("data-option-position")
      );

      if (!position) return;

      if (input.matches('input[type="radio"]')) {
        if (input.checked) {
          selected[position] = input.value;
        }

        return;
      }

      if (input.matches("select")) {
        selected[position] = input.value;
        return;
      }

      if (input.matches('input[type="hidden"]')) {
        selected[position] = input.value;
      }
    });

    return selected;
  }

  /**
   * Convert an option-value ID to its name.
   *
   * Example:
   * 15545077825885 -> "2 Windows"
   */
  function resolveOptionValue(
    variantData,
    position,
    value
  ) {
    const group = variantData.options?.find(
      option =>
        Number(option.position) === Number(position)
    );

    if (!group) {
      return String(value ?? "");
    }

    const match = group.values.find(
      optionValue =>
        String(optionValue.id) === String(value) ||
        String(optionValue.name) === String(value)
    );

    return match
      ? match.name
      : String(value ?? "");
  }

  /**
   * Determine availability of an option.
   *
   * Evaluates the candidate option against
   * selections in all other option groups.
   */
  function getOptionState(
    variantData,
    position,
    value,
    selectedOptions
  ) {
    const optionName = resolveOptionValue(
      variantData,
      position,
      value
    );

    const matchingVariants = variantData.variants.filter(
      variant => {
        if (
          variant.options[position - 1] !== optionName
        ) {
          return false;
        }

        return Object.entries(selectedOptions).every(
          ([selectedPosition, selectedValue]) => {
            const otherPosition = Number(selectedPosition);

            if (otherPosition === position) {
              return true;
            }

            const selectedName = resolveOptionValue(
              variantData,
              otherPosition,
              selectedValue
            );

            return (
              variant.options[otherPosition - 1] ===
              selectedName
            );
          }
        );
      }
    );

    if (!matchingVariants.length) {
      return STATES.INVALID;
    }

    if (
      matchingVariants.some(
        variant => variant.available
      )
    ) {
      return STATES.AVAILABLE;
    }

    return STATES.SOLD_OUT;
  }

  /**
   * Determine whether the currently selected
   * combination exists and is in stock.
   */
  function getSelectedCombinationState(
    variantData,
    selectedOptions
  ) {
    const optionGroups = variantData.options;

    const hasAllSelections = optionGroups.every(
      group => selectedOptions[group.position] != null
    );

    if (!hasAllSelections) {
      return null;
    }

    const selectedNames = optionGroups.map(group =>
      resolveOptionValue(
        variantData,
        group.position,
        selectedOptions[group.position]
      )
    );

    const matchingVariant = variantData.variants.find(
      variant =>
        variant.options.every(
          (value, index) => value === selectedNames[index]
        )
    );

    if (!matchingVariant) {
      return {
        state: STATES.INVALID,
        options: selectedNames
      };
    }

    return {
      state: matchingVariant.available
        ? STATES.AVAILABLE
        : STATES.SOLD_OUT,
      options: selectedNames
    };
  }

  /**
   * Find the label associated with an input.
   */
  function getInputLabel(input) {
    if (!input.id) return null;

    const picker = input.closest("variant-picker");

    return picker?.querySelector(
      `label[for="${CSS.escape(input.id)}"]`
    ) || null;
  }

  /**
   * Apply state to a radio option.
   */
  function updateRadioState(input, state) {
    const label = getInputLabel(input);

    if (!label) return;

    label.classList.remove(...STATE_CLASSES);

    label.classList.add(
      `variant-option--${state}`
    );

    input.dataset.availabilityState = state;

    /**
     * Remove Warehouse's native disabled styling
     * so our calculated availability state controls
     * the appearance.
     *
     * Do not change the input's checked or
     * disabled properties.
     */
    label.classList.remove(
      "block-swatch--disabled"
    );

    const nativeSwatch = label.closest(
      ".block-swatch--disabled"
    );

    if (nativeSwatch) {
      nativeSwatch.classList.remove(
        "block-swatch--disabled"
      );
    }
  }

  /**
   * Update dropdown options.
   */
  function updateSelectState(
    select,
    position,
    variantData,
    selectedOptions
  ) {
    Array.from(select.options).forEach(option => {
      if (!option.value) return;

      const state = getOptionState(
        variantData,
        position,
        option.value,
        selectedOptions
      );

      option.dataset.availabilityState = state;

      if (!option.dataset.originalText) {
        option.dataset.originalText = option.textContent;
      }

      if (state === STATES.SOLD_OUT) {
        option.textContent =
          `${option.dataset.originalText} - Sold out`;
      } else if (state === STATES.INVALID) {
        option.textContent =
          `${option.dataset.originalText} - Unavailable`;
      } else {
        option.textContent =
          option.dataset.originalText;
      }

      // Warehouse controls the actual selection.
      // Do not disable dropdown options here.
    });
  }

  /**
   * Find alternative option values.
   *
   * Alternatives are checked against all
   * currently selected option groups.
   */
  function getAlternativeOptions(
    variantData,
    position,
    value,
    selectedOptions
  ) {
    const optionName = resolveOptionValue(
      variantData,
      position,
      value
    );

    const alternatives = [];

    variantData.options.forEach(group => {
      const groupPosition = Number(group.position);

      if (groupPosition === position) {
        return;
      }

      const validValues = group.values.filter(candidate => {
        return variantData.variants.some(variant => {
          if (
            variant.options[position - 1] !== optionName
          ) {
            return false;
          }

          if (
            variant.options[groupPosition - 1] !==
            candidate.name
          ) {
            return false;
          }

          return Object.entries(selectedOptions).every(
            ([selectedPosition, selectedValue]) => {
              const otherPosition =
                Number(selectedPosition);

              if (
                otherPosition === position ||
                otherPosition === groupPosition
              ) {
                return true;
              }

              const selectedName = resolveOptionValue(
                variantData,
                otherPosition,
                selectedValue
              );

              return (
                variant.options[otherPosition - 1] ===
                selectedName
              );
            }
          );
        });
      });

      const selectedName = resolveOptionValue(
        variantData,
        groupPosition,
        selectedOptions[groupPosition]
      );

      const values = validValues
        .map(candidate => candidate.name)
        .filter(name => name !== selectedName);

      if (values.length) {
        alternatives.push({
          group: group.name,
          position: groupPosition,
          values
        });
      }
    });

    return alternatives;
  }

  /**
   * Generate availability messages.
   *
   * Priority:
   * 1. Selected combination is invalid
   * 2. Selected combination is sold out
   * 3. Other unavailable options
   */
  function buildAvailabilityMessages(
    variantData,
    selectedOptions
  ) {
    const messages = [];

    /**
     * STEP 1:
     * Check the currently selected combination.
     */
    const selectedCombination = getSelectedCombinationState(
      variantData,
      selectedOptions
    );

    if (
      selectedCombination &&
      selectedCombination.state !== STATES.AVAILABLE
    ) {
      const selectedNames = selectedCombination.options;

      /**
       * Selected combination is out of stock.
       */
      if (selectedCombination.state === STATES.SOLD_OUT) {
        return [{
          type: STATES.SOLD_OUT,
          option: selectedNames.join(" + "),
          context: "",
          alternatives: [],
          selectedCombination: true
        }];
      }

      /**
       * Selected combination doesn't exist.
       *
       * Find alternatives that would make
       * the selected combination valid.
       */
      const alternatives = [];

      variantData.options.forEach(group => {
        const position = Number(group.position);

        const validValues = group.values.filter(candidate => {
          return variantData.variants.some(variant => {
            return variant.options.every((value, index) => {
              if (index === position - 1) {
                return value === candidate.name;
              }

              return value === selectedNames[index];
            });
          });
        });

        const suggestions = validValues
          .map(value => value.name)
          .filter(
            name => name !== selectedNames[position - 1]
          );

        if (suggestions.length) {
          alternatives.push({
            group: group.name,
            position,
            values: suggestions
          });
        }
      });

      /**
       * Prioritise changing the final option group
       * when possible.
       *
       * For example:
       * 4 Windows + Single Door
       * -> Suggest Double Door.
       */
      alternatives.sort(
        (a, b) => b.position - a.position
      );

      return [{
        type: STATES.INVALID,
        option: selectedNames[0],
        context: selectedNames.slice(1).join(" and "),
        alternatives,
        selectedCombination: true
      }];
    }

    /**
     * STEP 2:
     * Selected combination is available.
     *
     * Explain any other unavailable options.
     */
    variantData.options.forEach(group => {
      const position = Number(group.position);

      group.values.forEach(optionValue => {
        const state = getOptionState(
          variantData,
          position,
          optionValue.id,
          selectedOptions
        );

        if (state === STATES.AVAILABLE) {
          return;
        }

        /**
         * Don't explain the selected option again.
         */
        if (
          String(selectedOptions[position]) ===
          String(optionValue.id)
        ) {
          return;
        }

        const otherSelections = variantData.options
          .filter(
            otherGroup =>
              Number(otherGroup.position) !== position
          )
          .map(otherGroup => {
            const selectedValue =
              selectedOptions[otherGroup.position];

            if (!selectedValue) return null;

            return resolveOptionValue(
              variantData,
              otherGroup.position,
              selectedValue
            );
          })
          .filter(Boolean);

        const context = otherSelections.join(" and ");

        /**
         * Other option is sold out.
         */
        if (state === STATES.SOLD_OUT) {
          messages.push({
            type: STATES.SOLD_OUT,
            option: optionValue.name,
            context,
            alternatives: [],
            selectedCombination: false
          });

          return;
        }

        /**
         * Other option is invalid.
         */
        const alternatives = getAlternativeOptions(
          variantData,
          position,
          optionValue.id,
          selectedOptions
        );

        messages.push({
          type: STATES.INVALID,
          option: optionValue.name,
          context,
          alternatives,
          selectedCombination: false
        });
      });
    });

    return messages;
  }

  /**
   * Append bold text safely.
   */
  function appendBold(parent, value) {
    const strong = document.createElement("strong");

    strong.textContent = value;

    parent.appendChild(strong);
  }

  /**
   * Append a list of alternatives.
   */
  function appendAlternatives(parent, values) {
    values.forEach((value, index) => {
      if (index > 0) {
        parent.append(
          document.createTextNode(
            index === values.length - 1
              ? " or "
              : ", "
          )
        );
      }

      appendBold(parent, value);
    });
  }

  /**
   * Create the availability message container.
   */
  function createMessageContainer(picker) {
    const container = document.createElement("div");

    container.className =
      "variant-availability-message";

    container.setAttribute("role", "status");
    container.setAttribute("aria-live", "polite");

    const icon = document.createElement("span");

    icon.className =
      "variant-availability-message__icon";

    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "ⓘ";

    const content = document.createElement("div");

    content.className =
      "variant-availability-message__content";

    const title = document.createElement("strong");

    title.className =
      "variant-availability-message__title";

    title.textContent = "Option availability";

    const text = document.createElement("div");

    text.className =
      "variant-availability-message__text";

    content.append(title, text);
    container.append(icon, content);

    picker.appendChild(container);

    return container;
  }

  /**
   * Render availability messages.
   *
   * Automatically changes the heading based
   * on whether the selected combination is
   * available, invalid or sold out.
   */
  function updateAvailabilityMessage(
    picker,
    variantData,
    selectedOptions
  ) {
    const messages = buildAvailabilityMessages(
      variantData,
      selectedOptions
    );

    let container = picker.querySelector(
      ".variant-availability-message"
    );

    /**
     * Hide the message when all options
     * are available.
     */
    if (!messages.length) {
      if (container) {
        container.hidden = true;
      }

      return;
    }

    /**
     * Create the container if necessary.
     */
    if (!container) {
      container = createMessageContainer(picker);
    }

    /**
     * Determine the message heading.
     */
    const selectedMessage = messages.find(
      message => message.selectedCombination
    );

    const title = container.querySelector(
      ".variant-availability-message__title"
    );

    if (title) {
      if (selectedMessage) {
        title.textContent =
          selectedMessage.type === STATES.SOLD_OUT
            ? "Currently out of stock"
            : "Combination unavailable";
      } else {
        title.textContent = "Option availability";
      }
    }

    const textContainer = container.querySelector(
      ".variant-availability-message__text"
    );

    textContainer.replaceChildren();

    /**
     * Render each message.
     */
    messages.forEach(message => {
      const item = document.createElement("div");

      item.className =
        "variant-availability-message__item";

      const description = document.createElement("p");

      description.className =
        "variant-availability-message__description";

      /**
       * Highlight the unavailable option.
       */
      appendBold(description, message.option);

      if (message.type === STATES.SOLD_OUT) {
        description.append(
          document.createTextNode(
            " is currently out of stock"
          )
        );
      } else {
        description.append(
          document.createTextNode(
            " is not available"
          )
        );
      }

      /**
       * Include the conflicting selection.
       */
      if (message.context) {
        description.append(
          document.createTextNode(" with ")
        );

        appendBold(description, message.context);
      }

      description.append(
        document.createTextNode(".")
      );

      item.appendChild(description);

      /**
       * Show suggested alternatives
       * for invalid combinations.
       */
      if (
        message.type === STATES.INVALID &&
        message.alternatives.length
      ) {
        const alternative = message.alternatives[0];

        const suggestion = document.createElement("p");

        suggestion.className =
          "variant-availability-message__suggestion";

        suggestion.append(
          document.createTextNode("Choose ")
        );

        appendAlternatives(
          suggestion,
          alternative.values
        );

        suggestion.append(
          document.createTextNode(" instead.")
        );

        item.appendChild(suggestion);
      }

      textContainer.appendChild(item);
    });

    container.hidden = false;
  }

  /**
   * Update a single picker.
   */
  function updatePicker(picker) {
    if (!picker.isConnected) return;

    const variantData = getProductData(picker);

    if (
      !variantData?.variants?.length ||
      !variantData?.options?.length
    ) {
      return;
    }

    const inputs = getOptionInputs(picker);

    if (!inputs.length) return;

    const selectedOptions =
      getSelectedOptions(inputs);

    /**
     * Update all option states.
     */
    inputs.forEach(input => {
      const position = Number(
        input.getAttribute("data-option-position")
      );

      if (!position) return;

      if (input.matches("select")) {
        updateSelectState(
          input,
          position,
          variantData,
          selectedOptions
        );

        return;
      }

      if (!input.matches('input[type="radio"]')) {
        return;
      }

      const state = getOptionState(
        variantData,
        position,
        input.value,
        selectedOptions
      );

      updateRadioState(input, state);
    });

    /**
     * Update explanatory message.
     */
    updateAvailabilityMessage(
      picker,
      variantData,
      selectedOptions
    );
  }

  /**
   * Update all pickers.
   */
  function updateAllPickers() {
    document.querySelectorAll(
      "variant-picker"
    ).forEach(updatePicker);
  }

  /**
   * Schedule an update.
   *
   * Multiple events in the same frame
   * are combined into one update.
   */
  let updateScheduled = false;

  function scheduleUpdate() {
    if (updateScheduled) return;

    updateScheduled = true;

    requestAnimationFrame(() => {
      updateScheduled = false;
      updateAllPickers();
    });
  }

  /**
   * Watch for Warehouse replacing variant
   * picker elements during product rerenders.
   *
   * Only child additions/removals are observed.
   * Attribute changes from our own styling
   * will not trigger another update.
   */
  function observeVariantPickers() {
    const observer = new MutationObserver(
      mutations => {
        const pickerChanged = mutations.some(
          mutation => {
            const nodes = [
              ...mutation.addedNodes,
              ...mutation.removedNodes
            ];

            return nodes.some(node => {
              if (!(node instanceof Element)) {
                return false;
              }

              return (
                node.matches("variant-picker") ||
                !!node.querySelector("variant-picker")
              );
            });
          }
        );

        if (pickerChanged) {
          scheduleUpdate();
        }
      }
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialise.
   */
  function init() {
    updateAllPickers();

    /**
     * Customer changes an option.
     *
     * We do not prevent the event or
     * interfere with Warehouse's handling.
     */
    document.addEventListener(
      "change",
      event => {
        if (
          event.target.matches(
            "[data-option-position]"
          )
        ) {
          scheduleUpdate();
        }
      }
    );

    /**
     * Warehouse rerender event.
     */
    document.addEventListener(
      "product:rerender",
      scheduleUpdate
    );

    /**
     * Shopify theme editor support.
     */
    document.addEventListener(
      "shopify:section:load",
      scheduleUpdate
    );

    observeVariantPickers();
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      init
    );
  } else {
    init();
  }
})();